import React, { useEffect, useMemo, useState } from "react"
import { Dropdown, Table, Select, DatePicker, message, Spin } from "antd"
import { DownOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import "dayjs/locale/ar"
import { ReferralsApi } from "../../Apis/referralsApi"
import { Discounts as DiscountsApi } from "../../Apis/discountsApi"
import { translations } from "../../utils/Translation/staticTranslation"

dayjs.extend(relativeTime)

const formatOverviewInt = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n.toLocaleString() : "0"
}

const formatOverviewDecimal = (value) => {
  const n = typeof value === "string" ? parseFloat(value) : Number(value)
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "0.00"
}

const formatRewardsEarned = (value) => `SAR ${formatOverviewDecimal(value)}`

const formatPendingHoldSubtitle = (overviewRow, template) => {
  if (overviewRow?.reward_hold_period_days == null) return ""
  const n = Number(overviewRow.reward_hold_period_days)
  if (!Number.isFinite(n) || n < 0) return ""
  const days = Math.trunc(n)
  return String(template).replace(/\{days\}/g, String(days))
}

const normalizeIsoCountryCode = (iso2) => {
  if (iso2 == null || iso2 === "") return ""
  const s = String(iso2).trim().toUpperCase()
  if (s.length !== 2 || !/^[A-Z]{2}$/.test(s)) return ""
  return s
}

const flagCdnUrl = (iso2) => {
  const c = normalizeIsoCountryCode(iso2)
  return c ? `https://flagcdn.com/${c.toLowerCase()}.svg` : ""
}

/** Lowercased region display name -> ISO alpha-2, built from Intl (no hand-maintained country names). */
const buildRegionDisplayNameIndex = (locale) => {
  const map = new Map()
  if (typeof Intl === "undefined" || !Intl.DisplayNames) return map
  let codes = []
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      codes = Intl.supportedValuesOf("region")
    }
  } catch (_) {
    codes = []
  }
  if (!Array.isArray(codes) || codes.length === 0) {
    for (let i = 65; i <= 90; i++) {
      for (let j = 65; j <= 90; j++) {
        codes.push(String.fromCharCode(i) + String.fromCharCode(j))
      }
    }
  }
  let display
  try {
    display = new Intl.DisplayNames([locale], { type: "region" })
  } catch (_) {
    return map
  }
  for (const code of codes) {
    const upper = String(code).trim().toUpperCase()
    if (upper.length !== 2 || !/^[A-Z]{2}$/.test(upper)) continue
    let label
    try {
      label = display.of(upper)
    } catch (_) {
      continue
    }
    if (!label || typeof label !== "string") continue
    const k = label.trim().toLowerCase()
    if (!map.has(k)) map.set(k, upper)
  }
  return map
}

const regionDisplayNameIndexByLocale = new Map()

const getRegionDisplayNameIndex = (locale) => {
  if (regionDisplayNameIndexByLocale.has(locale)) {
    return regionDisplayNameIndexByLocale.get(locale)
  }
  const idx = buildRegionDisplayNameIndex(locale)
  regionDisplayNameIndexByLocale.set(locale, idx)
  return idx
}

/** Match API country string (e.g. top-referrals `country`) to ISO2 for flagcdn when no code field exists. */
const iso2FromApiCountryLabel = (label, lang) => {
  const trimmed = typeof label === "string" ? label.trim() : ""
  if (!trimmed || trimmed === "—") return ""
  const key = trimmed.toLowerCase()
  const tryLocales = String(lang || "").toLowerCase().startsWith("ar")
    ? ["ar", "en"]
    : ["en", "ar"]
  for (const loc of tryLocales) {
    const code = getRegionDisplayNameIndex(loc).get(key)
    if (code) return code
  }
  return ""
}

const topReferralRowIso2FromCodeFields = (row) => {
  const codeFields = [
    row?.country_code,
    row?.iso2,
    row?.country_iso2,
    row?.countryCode,
    row?.country_iso,
  ]
  for (const raw of codeFields) {
    const n = normalizeIsoCountryCode(raw)
    if (n) return n
  }
  for (const raw of [
    row?.country,
    row?.country_name,
    row?.country_display,
    row?.display_country,
  ]) {
    const n = normalizeIsoCountryCode(raw)
    if (n) return n
  }
  return ""
}

const topReferralCountryLabelFromApi = (row) =>
  [row?.country, row?.country_name, row?.country_display, row?.display_country]
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .find(Boolean) || "—"

const activityFilterKeyToApi = (key) => {
  if (!key || key === "all") return undefined
  return key
}

const activityImg = (src, alt = "") => (
  <img src={src} alt={alt} className="ref-activity-icon-img" />
)

/** Map API `activity_type` to dashboard feed icon + color */
const activityTypeVisual = (type) => {
  const t = String(type || "")
    .toLowerCase()
    .replace(/-/g, "_")
  const map = {
    signup: { icon: activityImg("/referrals/signed-up-via-referral.svg"), cls: "blue" },
    subscription: { icon: activityImg("/referrals/subscribed-ref.svg"), cls: "green" },
    pending_approval: {
      icon: activityImg("/referrals/pending-approval.svg"),
      cls: "orange",
    },
    redeem: { icon: activityImg("/referrals/redeem-wallet.svg"), cls: "purple" },
    redeem_points: { icon: activityImg("/referrals/redeem-wallet.svg"), cls: "purple" },
    redeem_wallet: { icon: activityImg("/referrals/redeem-wallet.svg"), cls: "purple" },
    conversion: { icon: activityImg("/referrals/successful-conversion.svg"), cls: "green" },
    successful_conversion: {
      icon: activityImg("/referrals/successful-conversion.svg"),
      cls: "green",
    },
    link_click: { icon: activityImg("/referrals/referral-link-clicked.svg"), cls: "blue" },
    referral_link_clicked: {
      icon: activityImg("/referrals/referral-link-clicked.svg"),
      cls: "blue",
    },
    reward_claimed: { icon: activityImg("/referrals/reward-claimed.svg"), cls: "green" },
    email_sent: { icon: activityImg("/referrals/referral-email-sent.svg"), cls: "purple" },
    referral_email_sent: {
      icon: activityImg("/referrals/referral-email-sent.svg"),
      cls: "purple",
    },
    social_share: { icon: activityImg("/referrals/social-share.svg"), cls: "blue" },
    conversion_failed: { icon: activityImg("/referrals/conversion-failed.svg"), cls: "red" },
  }
  if (map[t]) return map[t]
  return {
    icon: activityImg("/referrals/new-user-signup.svg"),
    cls: "orange",
  }
}

const buildActivityDetailText = (row, lang) => {
  const isAr = String(lang || "").toLowerCase().startsWith("ar")
  const title = (
    isAr
      ? (row.title_arabic || "").trim() || (row.title || "").trim()
      : (row.title || "").trim()
  )
  if (title) return title

  if (row.activity_type === "subscription" && row.plan_name) {
    return `subscribed - ${row.plan_name}`
  }

  const details = (row.details || "").trim()
  const label = (row.activity_label || "").trim()
  if (details && label && details !== label) return `${label} — ${details}`
  if (details || label) return details || label

  const type = String(row.activity_type || "")
    .replace(/_/g, " ")
    .trim()
  return type || "—"
}

const formatActivityRewardPoints = (amount) => {
  const n = typeof amount === "string" ? parseFloat(amount) : Number(amount)
  if (!Number.isFinite(n) || n === 0) return ""
  const abs = Math.abs(n)
  const text = Number.isInteger(abs) ? String(abs) : abs.toFixed(2)
  if (n > 0) return `+${text} pts`
  return `-${text} pts`
}

const parseActivityAmount = (value) => {
  if (value == null || value === "") return NaN
  return typeof value === "string" ? parseFloat(value) : Number(value)
}

/** Redemptions are shown as deductions (always minus), not as positive rewards. */
const formatActivityRedeemedPoints = (amount) => {
  const n = typeof amount === "string" ? parseFloat(amount) : Number(amount)
  if (!Number.isFinite(n) || n === 0) return ""
  const abs = Math.abs(n)
  const text = Number.isInteger(abs) ? String(abs) : abs.toFixed(2)
  return `-${text} pts`
}

const mapActivityLogRow = (row, lang) => {
  const visual = activityTypeVisual(row.activity_type)
  const redeemedN = parseActivityAmount(row.redeemed_amount)
  const showRedeemed = Number.isFinite(redeemedN) && redeemedN !== 0
  const pointsFromReward = formatActivityRewardPoints(row.reward_amount)
  const points = showRedeemed
    ? formatActivityRedeemedPoints(redeemedN)
    : pointsFromReward
  const pointsClass = showRedeemed
    ? "ref-points-redeemed"
    : points && points.startsWith("-")
      ? "ref-points-negative"
      : "ref-points-positive"

  return {
    key: row.id ?? `${row.created_at}-${row.user_email}`,
    name: row.user_name || "—",
    email: (row.user_email || "").trim(),
    detail: buildActivityDetailText(row, lang),
    points,
    pointsClass: points ? pointsClass : "",
    time: row.created_at
      ? dayjs(row.created_at)
          .locale(lang === "ar" ? "ar" : "en")
          .fromNow()
      : "",
    icon: visual.icon,
    iconClass: visual.cls,
  }
}

const rankClass = (rank) => {
  if (rank === 1) return "rank-one"
  if (rank === 2) return "rank-two"
  if (rank === 3) return "rank-three"
  return "rank-default"
}

const mapTopReferralRow = (row, lang) => {
  const countryLabel = topReferralCountryLabelFromApi(row)
  const isoFromCodes = topReferralRowIso2FromCodeFields(row)
  const iso =
    isoFromCodes ||
    (countryLabel !== "—" ? iso2FromApiCountryLabel(countryLabel, lang) : "")
  return {
    key: row.referrer_id ?? row.rank,
    rank: row.rank,
    name: row.referrer_name,
    email: row.email,
    referrals: row.referrals,
    conversions: row.conversions,
    rewards: formatRewardsEarned(row.rewards_earned),
    country: countryLabel,
    countryFlagUrl: flagCdnUrl(iso),
  }
}

const DashboardTab = ({ onViewAllRewards, readOnly = false }) => {
  const { RangePicker } = DatePicker
  const lang = localStorage.getItem("language") || "ar"
  const isArabicLang = String(lang).toLowerCase().startsWith("ar")
  const t = translations[lang]?.referralsPage || translations.en.referralsPage
  const readOnlyBlockStyle = readOnly
    ? { pointerEvents: "none", opacity: 0.65 }
    : undefined

  const [overview, setOverview] = useState(null)
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [topReferralsRows, setTopReferralsRows] = useState([])
  const [topReferralsLoading, setTopReferralsLoading] = useState(true)
  const [topReferralsCountry, setTopReferralsCountry] = useState("")
  const [topReferralFilterCountries, setTopReferralFilterCountries] = useState([])
  const [activityRows, setActivityRows] = useState([])
  const [activityLoading, setActivityLoading] = useState(true)
  const [activityFilterKey, setActivityFilterKey] = useState("all")
  const [dashboardDateFilterKey, setDashboardDateFilterKey] = useState("last30days")
  const [dashboardCustomRange, setDashboardCustomRange] = useState({
    startDate: null,
    endDate: null,
  })

  const dashboardDateFilterItems = useMemo(
    () => [
      { key: "last90days", label: t.filter_last_90_days },
      { key: "last30days", label: t.filter_last_30_days },
      { key: "last15days", label: t.filter_last_15_days },
      { key: "last7days", label: t.filter_last_7_days },
      { key: "customdate", label: t.filter_custom_range },
    ],
    [t],
  )

  const overviewCardDefs = useMemo(
    () => [
      {
        title: t.overview_total_referrals,
        icon: <img src="/referrals/link-ref.svg" alt="" />,
        iconClass: "violet",
        format: formatOverviewInt,
        getValue: (r) => r?.total_referrals,
      },
      {
        title: t.overview_people_from_referrals,
        icon: <img src="/referrals/user-group.svg" alt="" />,
        iconClass: "blue",
        format: formatOverviewInt,
        getValue: (r) => r?.people_from_referrals,
      },
      {
        title: t.overview_total_rewards_earned,
        icon: <img src="/referrals/star-circle-admin.svg" alt="" />,
        iconClass: "orange",
        format: formatOverviewDecimal,
        getValue: (r) => r?.total_rewards_earned,
      },
      {
        title: t.overview_points_redeemed,
        icon: <img src="/referrals/star-circle-ref-ad.svg" alt="" />,
        iconClass: "green",
        format: formatOverviewDecimal,
        getValue: (r) => r?.points_redeemed,
      },
      {
        title: t.overview_pending_approvals,
        icon: <img src="/referrals/time-half-pass.svg" alt="" />,
        iconClass: "red",
        format: formatOverviewInt,
        getValue: (r) => r?.pending_approvals,
        getSubtitle: (r) => formatPendingHoldSubtitle(r, t.overview_pending_subtitle),
      },
    ],
    [t],
  )

  const activityFilterMenuItems = useMemo(
    () => [
      { key: "all", label: t.activity_filter_all },
      { key: "signup", label: t.activity_filter_signup },
      { key: "subscription", label: t.activity_filter_subscription },
      { key: "pending_approval", label: t.activity_filter_pending_approval },
      { key: "redeem_points", label: t.activity_filter_redeem_points },
    ],
    [t],
  )

  const topReferralsColumns = useMemo(
    () => [
      {
        title: t.col_rank,
        dataIndex: "rank",
        key: "rank",
        width: 80,
        align: "center",
        render: (rank) => (
          <span className={`ref-rank-dot ${rankClass(rank)}`}>{rank}</span>
        ),
      },
      { title: t.col_referrer, dataIndex: "name", key: "name" },
      { title: t.col_email, dataIndex: "email", key: "email" },
      {
        title: t.col_referrals,
        dataIndex: "referrals",
        key: "referrals",
        width: 120,
      },
      {
        title: t.col_conversions,
        dataIndex: "conversions",
        key: "conversions",
        width: 130,
        render: (value) => <span className="ref-highlight">{value}</span>,
      },
      {
        title: t.col_rewards_earned,
        dataIndex: "rewards",
        key: "rewards",
        width: 150,
      },
      {
        title: t.col_country,
        dataIndex: "country",
        key: "country",
        width: 170,
        render: (_, row) => (
          <span className="ref-country-cell">
            {row.countryFlagUrl ? (
              <img
                src={row.countryFlagUrl}
                alt=""
                className="ref-country-flag"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.style.display = "none"
                }}
              />
            ) : null}
            {row.country}
          </span>
        ),
      },
    ],
    [t],
  )

  const topReferralsCountryOptions = useMemo(
    () => [
      { label: t.country_option_all, value: "" },
      ...topReferralFilterCountries,
    ],
    [t.country_option_all, topReferralFilterCountries],
  )

  const activityFilterButtonLabel = useMemo(() => {
    return (
      activityFilterMenuItems.find((i) => i.key === activityFilterKey)?.label ??
      t.activity_filter_all
    )
  }, [activityFilterKey, activityFilterMenuItems, t.activity_filter_all])

  const dashboardDateFilterLabel = useMemo(() => {
    return (
      dashboardDateFilterItems.find((item) => item.key === dashboardDateFilterKey)
        ?.label ?? t.filter_last_30_days
    )
  }, [dashboardDateFilterKey, dashboardDateFilterItems, t.filter_last_30_days])

  const dashboardFilterQuery = useMemo(() => {
    if (dashboardDateFilterKey === "customdate") {
      if (!dashboardCustomRange.startDate || !dashboardCustomRange.endDate) return null
      return {
        filterType: "customdate",
        startDate: dashboardCustomRange.startDate,
        endDate: dashboardCustomRange.endDate,
      }
    }

    return {
      filterType: dashboardDateFilterKey,
    }
  }, [dashboardDateFilterKey, dashboardCustomRange])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!dashboardFilterQuery) return
      setOverviewLoading(true)
      try {
        const res = await ReferralsApi.getDashboardOverview(dashboardFilterQuery)
        if (cancelled) return
        const payload = res.data
        if (payload?.success && payload.result) {
          setOverview(payload.result)
        } else {
          setOverview(null)
          message.error("Could not load referral dashboard overview.")
        }
      } catch (e) {
        if (!cancelled) {
          setOverview(null)
          message.error(
            e?.response?.data?.message ||
              e?.response?.data?.error ||
              e?.message ||
              "Failed to load overview."
          )
        }
      } finally {
        if (!cancelled) setOverviewLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dashboardFilterQuery])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const response = await DiscountsApi.getCountryFilter()
        const countryCodes = response?.data?.result?.available_countries || []
        const formatter = new Intl.DisplayNames(
          [isArabicLang ? "ar" : "en"],
          { type: "region" },
        )
        const opts = countryCodes
          .map((code) => {
            const cc = String(code).trim().toUpperCase()
            if (!normalizeIsoCountryCode(cc)) return null
            const name = formatter.of(cc) || cc
            return { label: name, value: cc.toLowerCase() }
          })
          .filter(Boolean)
          .sort((a, b) =>
            a.label.localeCompare(b.label, isArabicLang ? "ar" : "en"),
          )
        if (!cancelled) setTopReferralFilterCountries(opts)
      } catch (_) {
        if (!cancelled) setTopReferralFilterCountries([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isArabicLang])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setTopReferralsLoading(true)
      try {
        const res = await ReferralsApi.getTopReferrals({
          country: topReferralsCountry || undefined,
          filterType: dashboardFilterQuery?.filterType,
          startDate: dashboardFilterQuery?.startDate,
          endDate: dashboardFilterQuery?.endDate,
        })
        if (cancelled) return
        const payload = res.data
        if (payload?.success && payload.result?.results) {
          setTopReferralsRows(
            payload.result.results.map((r) => mapTopReferralRow(r, lang)),
          )
        } else {
          setTopReferralsRows([])
          message.error("Could not load top referrals.")
        }
      } catch (e) {
        if (!cancelled) {
          setTopReferralsRows([])
          message.error(
            e?.response?.data?.message ||
              e?.response?.data?.error ||
              e?.message ||
              "Failed to load top referrals."
          )
        }
      } finally {
        if (!cancelled) setTopReferralsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [topReferralsCountry, dashboardFilterQuery, lang])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setActivityLoading(true)
      try {
        const res = await ReferralsApi.getRecentActivity({
          type: activityFilterKeyToApi(activityFilterKey),
          filterType: dashboardFilterQuery?.filterType,
          startDate: dashboardFilterQuery?.startDate,
          endDate: dashboardFilterQuery?.endDate,
        })
        if (cancelled) return
        const payload = res.data
        if (payload?.success && payload.result?.results) {
          setActivityRows(payload.result.results.map((r) => mapActivityLogRow(r, lang)))
        } else {
          setActivityRows([])
          message.error("Could not load recent activity.")
        }
      } catch (e) {
        if (!cancelled) {
          setActivityRows([])
          message.error(
            e?.response?.data?.message ||
              e?.response?.data?.error ||
              e?.message ||
              "Failed to load activity."
          )
        }
      } finally {
        if (!cancelled) setActivityLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activityFilterKey, dashboardFilterQuery, lang])

  const overviewCards = useMemo(() => {
    return overviewCardDefs.map((def) => {
      const hasData = overview != null
      const raw = hasData ? def.getValue(overview) : null
      const emptyFallback = def.format === formatOverviewInt ? 0 : "0.00"
      const value =
        overviewLoading && !hasData
          ? "—"
          : def.format(raw ?? emptyFallback)

      const card = {
        title: def.title,
        icon: def.icon,
        iconClass: def.iconClass,
        value,
      }
      if (typeof def.getSubtitle === "function" && hasData) {
        const sub = def.getSubtitle(overview)
        if (sub) card.subtitle = sub
      }
      return card
    })
  }, [overview, overviewLoading, overviewCardDefs])

  return (
    <div className="referrals-dashboard-view">
      <div className="referrals-subheader">
        <h2>{t.dashboard_overview}</h2>
        <div className="referrals-actions" style={readOnlyBlockStyle}>
          {dashboardDateFilterKey === "customdate" && (
            <RangePicker
              allowClear={false}
              className="ref-dashboard-custom-range"
              format="YYYY-MM-DD"
              placeholder={[t.date_start, t.date_end]}
              value={
                dashboardCustomRange.startDate && dashboardCustomRange.endDate
                  ? [dayjs(dashboardCustomRange.startDate), dayjs(dashboardCustomRange.endDate)]
                  : null
              }
              onChange={(dates) => {
                if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
                  setDashboardCustomRange({ startDate: null, endDate: null })
                  return
                }

                const start = dates[0].format("YYYY-MM-DD")
                const end = dates[1].format("YYYY-MM-DD")
                setDashboardCustomRange({
                  startDate: start,
                  endDate: end,
                })
              }}
            />
          )}
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            overlayClassName="ref-dashboard-date-dropdown"
            menu={{
              items: dashboardDateFilterItems,
              selectedKeys: [dashboardDateFilterKey],
              onClick: ({ key }) => {
                setDashboardDateFilterKey(key)
                if (key !== "customdate") {
                  setDashboardCustomRange({ startDate: null, endDate: null })
                }
              },
            }}
          >
            <button className="ref-button-secondary ref-dashboard-date-btn" type="button">
              <img
                src="/referrals/filter-horizontal.svg"
                alt=""
                width={16}
                height={16}
                className="ref-action-icon"
              />
              {dashboardDateFilterLabel}
              <DownOutlined />
            </button>
          </Dropdown>
        </div>
      </div>

      <div className="ref-overview-grid">
        {overviewCards.map((card) => (
          <div key={card.title} className="ref-overview-card">
            <div className={`ref-overview-icon ${card.iconClass}`}>
              {card.icon}
            </div>
            <h3>
              <span className="ref-overview-metric-value" dir="ltr" translate="no">
                {card.value}
              </span>
              {card.subtitle && (
                <span className="ref-overview-value-subtitle">{card.subtitle}</span>
              )}
            </h3>
            <p>{card.title}</p>
          </div>
        ))}
      </div>

      <div className="ref-panel ref-top-referrals-panel">
        <div className="ref-panel-header ref-top-referrals-header">
          <h3>{t.top_referrals}</h3>
          <div className="ref-panel-header-right">
            <div style={readOnlyBlockStyle}>
            <Select
              value={topReferralsCountry}
              onChange={setTopReferralsCountry}
              placeholder={t.country_placeholder}
              className="ref-country-filter user-table-select"
              style={{
                width: 140,
                height: "36px",
                backgroundColor: "#F9FAFB",
                borderRadius: "8px",
              }}
              options={topReferralsCountryOptions}
            />
            </div>
            <button
              type="button"
              className="ref-link-btn"
              onClick={() => onViewAllRewards?.()}
            >
              {t.view_all}
            </button>
          </div>
        </div>
        <div className="responsive-table-wrapper ref-top-referrals-table-wrap">
          <Table
            className="ref-ant-table ref-top-referrals-table"
            columns={topReferralsColumns}
            dataSource={topReferralsRows}
            loading={topReferralsLoading}
            pagination={false}
            size="middle"
          />
        </div>
      </div>

      <div className="ref-panel ref-recent-activity-panel">
        <div className="ref-panel-header ref-recent-activity-header">
          <h3>{t.recent_activity}</h3>
          <div style={readOnlyBlockStyle}>
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            overlayClassName="ref-activity-type-dropdown"
            menu={{
              items: activityFilterMenuItems,
              selectedKeys: [activityFilterKey],
              onClick: ({ key }) => setActivityFilterKey(key),
            }}
          >
            <button type="button" className="ref-button-secondary ref-recent-activity-btn">
              {activityFilterButtonLabel}
              <DownOutlined />
            </button>
          </Dropdown>
          </div>
        </div>
        <div className="ref-activity-list">
          {activityLoading ? (
            <div className="ref-activity-loading">
              <Spin size="small" />
            </div>
          ) : activityRows.length === 0 ? (
            <p className="ref-activity-empty">{t.activity_empty}</p>
          ) : (
            activityRows.map((item) => (
              <div className="ref-activity-item" key={item.key}>
                <div className={`ref-activity-icon ${item.iconClass}`}>
                  {item.icon}
                </div>
                <div
                  className={`ref-activity-content${isArabicLang ? " ref-activity-content--ar" : ""}`}
                >
                  {isArabicLang ? (
                    <>
                      {/*
                        dir=rtl on flex: main-start is on the right. DOM order detail → email → name
                        + justify-content: start places detail at the right for RTL reading order.
                      */}
                      <div className="ref-activity-main-line ref-activity-main-line--ar" dir="rtl">
                        <span className="ref-activity-detail" dir="auto">
                          {item.detail}
                        </span>
                        {item.email ? (
                          <span className="ref-activity-email" dir="ltr">
                            {" "}
                            ({item.email})
                          </span>
                        ) : null}
                        <strong className="ref-activity-name" dir="ltr">
                          {item.name}
                        </strong>
                      </div>
                      {item.time ? (
                        <p className="ref-activity-time ref-activity-time--ar" dir="rtl">
                          {item.time}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <strong>{item.name}</strong>
                      {item.email && (
                        <span className="ref-activity-email"> ({item.email})</span>
                      )}{" "}
                      {item.detail}
                      {item.time && <p>{item.time}</p>}
                    </>
                  )}
                </div>
                {item.points ? (
                  <span className={item.pointsClass} dir="ltr">
                    {item.points}
                  </span>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default DashboardTab
